/**
 * Global Sticky Product Bar — appears on non-product pages (homepage,
 * collection, etc.) promoting a featured product.
 *
 * Key changes:
 * - Uses a fixed featured product provided by theme settings
 * - Shared desktop/mobile visibility with editor-safe preview behavior
 * - Dismiss resets on refresh/navigation instead of persisting in storage
 */

class GlobalStickyProductBar extends HTMLElement {
  constructor() {
    super();
    this.threshold = 220;
    this.isVisible = false;
    this.isAdding = false;
    this.isDismissed = false;
    this.handleResize = this.handleResize.bind(this);
    this.handleScroll = this.handleScroll.bind(this);
    this.handleAddToCart = this.handleAddToCart.bind(this);
    this.handleDismiss = this.handleDismiss.bind(this);
  }

  connectedCallback() {
    const thresholdAttr = Number.parseInt(this.dataset.threshold || '', 10);
    if (!Number.isNaN(thresholdAttr)) {
      this.threshold = thresholdAttr;
    }

    this.blockedPaths = ['/cart', '/challenge', '/products/'];
    this.designMode = this.dataset.designMode === 'true' || Boolean(window.Shopify?.designMode);
    this.button = this.querySelector('.global-sticky-product-bar__button');
    this.dismissBtn = this.querySelector('.global-sticky-product-bar__dismiss');
    this.titleEl = this.querySelector('.global-sticky-product-bar__title');
    this.titleLink = this.querySelector('.global-sticky-product-bar__title-link');
    this.mediaLink = this.querySelector('.global-sticky-product-bar__media-link');
    this.imageEl = this.querySelector('.global-sticky-product-bar__image');
    this.variantEl = this.querySelector('.global-sticky-product-bar__variant');
    this.priceEl = this.querySelector('.global-sticky-product-bar__price');
    this.currentState = this.getDefaultState();

    if (!this.button || !this.currentState.variantId || this.isBlockedPath()) {
      this.remove();
      return;
    }

    this.buttonLabel = this.button.textContent.trim() || 'ADD TO CART';
    this.button.addEventListener('click', this.handleAddToCart);
    this.dismissBtn?.addEventListener('click', this.handleDismiss);
    window.addEventListener('scroll', this.handleScroll, { passive: true });
    window.addEventListener('resize', this.handleResize, { passive: true });

    this.renderState();
    this.updateOffset();
    this.updateVisibility();
  }

  disconnectedCallback() {
    this.button?.removeEventListener('click', this.handleAddToCart);
    this.dismissBtn?.removeEventListener('click', this.handleDismiss);
    window.removeEventListener('scroll', this.handleScroll);
    window.removeEventListener('resize', this.handleResize);
  }

  getDefaultState() {
    return {
      productHandle: this.dataset.defaultProductHandle || '',
      productTitle: this.normalizeProductTitle(this.dataset.defaultProductTitle || ''),
      productUrl: this.normalizeProductUrl(this.dataset.defaultProductUrl || '/'),
      variantId: this.dataset.defaultVariantId || '',
      variantTitle: this.normalizeVariantTitle(this.dataset.defaultVariantTitle || ''),
      price: this.dataset.defaultPrice || '',
      available: this.dataset.defaultAvailable !== 'false',
      image: this.dataset.defaultImage || '',
    };
  }

  normalizeProductTitle(value) {
    if (!value) return '';

    let normalized = String(value)
      .replace(/<br\s*\/?>/gi, ' ')
      .replace(/[⭐🔥📍💧🌿🌱✨💪🌼🩵🤍🐰⚡]/g, ' ')
      .replace(/\s+/g, ' ')
      .replace(/\(\s+/g, '(')
      .replace(/\s+\)/g, ')')
      .trim();

    if (/best seller duo/i.test(normalized)) return 'BEST SELLER DUO';
    if (/lash lover duo/i.test(normalized)) return 'LASH LOVER DUO';
    if (/clean\s*&\s*shape set/i.test(normalized)) return 'CLEAN & SHAPE SET';
    if (/shampoo holiday bogo|buy 1|get 1 free/i.test(normalized)) return 'SHAMPOO DUO';

    return normalized;
  }

  normalizeVariantTitle(value) {
    if (!value || value === 'Default Title') return '';
    return String(value).replace(/\s+/g, ' ').trim();
  }

  normalizeProductUrl(value) {
    if (!value) return '/';
    try {
      return new URL(value, window.location.origin).pathname;
    } catch (error) {
      return value;
    }
  }

  renderState() {
    const state = this.currentState;
    const productTitle = state.productTitle;
    const productUrl = state.productUrl;
    const variantTitle = state.variantTitle;

    if (this.titleEl && productTitle) {
      this.titleEl.textContent = productTitle;
    }

    if (this.titleLink && productUrl) {
      this.titleLink.href = productUrl;
    }

    if (this.mediaLink && productUrl) {
      this.mediaLink.href = productUrl;
    }

    if (this.imageEl && state.image) {
      this.imageEl.src = state.image;
      this.imageEl.alt = productTitle || 'Featured product';
      this.imageEl.removeAttribute('hidden');
    }

    if (this.variantEl) {
      this.variantEl.textContent = variantTitle || 'Ready to add';
    }

    if (this.priceEl && state.price) {
      this.priceEl.textContent = state.price;
    }

    this.button.disabled = !state.available || !state.variantId;
    this.button.textContent = state.available ? this.buttonLabel : 'SOLD OUT';
  }

  isBlockedPath() {
    return this.blockedPaths.some((path) => window.location.pathname.startsWith(path));
  }

  isPopupOpen() {
    return document.body.classList.contains('is-email-popup-open') || Boolean(document.querySelector('#email-popup.is-active'));
  }

  isOverlayOpen() {
    return Boolean(document.querySelector('#search-modal dialog[open], .cart-drawer__dialog[open]'));
  }

  handleScroll() {
    this.updateOffset();
    this.updateVisibility();
  }

  handleResize() {
    this.updateOffset();
    this.updateVisibility();
  }

  handleDismiss() {
    this.isDismissed = true;
    this.hide();
  }

  updateVisibility() {
    if (this.isPopupOpen() || this.isOverlayOpen() || this.isBlockedPath() || this.isDismissed) {
      this.hide();
      return;
    }

    if (this.designMode) {
      this.show();
      return;
    }

    const shouldShow = window.scrollY > this.threshold;

    if (shouldShow) {
      this.show();
    } else {
      this.hide();
    }
  }

  updateOffset() {
    const obstructionSelectors = [
      '.shopify-pc__banner',
      '#shopify-pc__banner',
      '.cc-window',
      '.cookie-bar',
      '[data-cookie-banner]',
      '.intercom-lightweight-app-launcher',
      '.tidio-chat',
      '.chat-widget-launcher',
    ];

    let maxOffset = 0;

    for (const selector of obstructionSelectors) {
      for (const element of document.querySelectorAll(selector)) {
        const style = window.getComputedStyle(element);
        if (style.display === 'none' || style.visibility === 'hidden' || style.position !== 'fixed') continue;

        const rect = element.getBoundingClientRect();
        if (rect.height <= 0) continue;

        if (rect.bottom >= window.innerHeight - 2) {
          maxOffset = Math.max(maxOffset, rect.height + 12);
        }
      }
    }

    this.style.setProperty('--global-sticky-product-bar-offset', `${maxOffset}px`);
  }

  show() {
    if (this.isVisible) return;
    this.isVisible = true;
    this.classList.add('global-sticky-product-bar--visible');
  }

  hide() {
    if (!this.isVisible) return;
    this.isVisible = false;
    this.classList.remove('global-sticky-product-bar--visible');
  }

  async handleAddToCart() {
    if (this.isAdding || !this.currentState.variantId || !this.currentState.available) return;

    this.isAdding = true;
    this.button.disabled = true;
    this.button.textContent = 'ADDING...';

    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({
          id: Number(this.currentState.variantId),
          quantity: 1,
        }),
      });

      if (!response.ok) {
        throw new Error(`Cart add failed with status ${response.status}`);
      }

      document.dispatchEvent(
        new CustomEvent('cart:update', {
          bubbles: true,
          detail: {
            data: {
              source: 'global-sticky-product-bar',
              variantId: String(this.currentState.variantId),
            },
          },
        })
      );
      document.dispatchEvent(new CustomEvent('cart:updated'));

      this.button.textContent = 'ADDED';

      const cartDrawer = document.querySelector('cart-drawer-component');
      if (cartDrawer?.open) {
        cartDrawer.open();
      } else {
        window.location.assign('/cart');
      }
    } catch (error) {
      console.error('Global sticky product bar add-to-cart failed', error);
      this.button.textContent = 'VIEW PRODUCT';
      if (this.currentState.productUrl) {
        window.setTimeout(() => {
          window.location.assign(this.currentState.productUrl);
        }, 300);
      }
    } finally {
      window.setTimeout(() => {
        this.isAdding = false;
        this.renderState();
      }, 1200);
    }
  }
}

if (!customElements.get('global-sticky-product-bar')) {
  customElements.define('global-sticky-product-bar', GlobalStickyProductBar);
}
